/**
 * Statistics Card Component
 * Reusable card for displaying dashboard statistics
 */

import React from 'react';
import { 
  TruckIcon, 
  ClockIcon, 
  UserGroupIcon, 
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

const StatCard = ({ 
  title, 
  value, 
  icon: Icon, 
  color = 'blue', 
  trend = null, 
  subtitle = null,
  loading = false,
  onClick = null 
}) => {
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
    gray: 'text-gray-600'
  };

  const trendColors = {
    up: 'text-green-600 bg-green-100',
    down: 'text-red-600 bg-red-100',
    neutral: 'text-gray-600 bg-gray-100'
  };

  const TrendIcon = trend?.direction === 'up' ? ArrowTrendingUpIcon : 
                   trend?.direction === 'down' ? ArrowTrendingDownIcon : MinusIcon;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className={`bg-white rounded-lg shadow p-6 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow duration-200' : ''}`}
      onClick={handleClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            {loading ? (
              <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
            ) : (
              <Icon className={`h-8 w-8 ${colorClasses[color]}`} />
            )}
          </div>
          <div className="ml-4 flex-1">
            <p className="text-sm font-medium text-gray-500">{title}</p>
            {loading ? (
              <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mt-1"></div>
            ) : (
              <p className="text-2xl font-semibold text-gray-900">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
            )}
            {subtitle && !loading && (
              <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
            )}
          </div>
        </div>
        
        {trend && !loading && (
          <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${trendColors[trend.direction]}`}>
            <TrendIcon className="h-3 w-3 mr-1" />
            {trend.value}
          </div>
        )}
      </div>
    </div>
  );
};

// Predefined stat card types
export const InventoryStatCard = ({ stats, loading, onClick }) => (
  <StatCard
    title="Available Items"
    value={stats?.statusCounts?.AVAILABLE || 0}
    icon={TruckIcon}
    color="green"
    subtitle={`${stats?.total || 0} total items`}
    loading={loading}
    onClick={onClick}
  />
);

export const ReservationStatCard = ({ stats, loading, onClick }) => (
  <StatCard
    title="Active Reservations"
    value={stats?.active || 0}
    icon={ClockIcon}
    color="blue"
    subtitle={`${stats?.total || 0} total today`}
    loading={loading}
    onClick={onClick}
  />
);

export const CustomerStatCard = ({ stats, loading, onClick }) => (
  <StatCard
    title="Total Customers"
    value={stats?.total || 0}
    icon={UserGroupIcon}
    color="purple"
    subtitle="All time"
    loading={loading}
    onClick={onClick}
  />
);

export const RevenueStatCard = ({ stats, loading, onClick }) => (
  <StatCard
    title="Today's Revenue"
    value={`$${(stats?.totalAmount || 0).toFixed(2)}`}
    icon={CurrencyDollarIcon}
    color="green"
    subtitle="Today's total"
    loading={loading}
    onClick={onClick}
  />
);

export default StatCard;